import type { CSSProperties } from 'react'
import type { RemoteAudioTrack } from 'livekit-client'
import { MicOff } from 'lucide-react'
import { BADGER_FRAMES, BADGER_FRAME_URLS, useBadgerFrame, type BadgerFrame } from '@/lib/watch/badgerFrames'
import type { CohostState } from '@/lib/watch/useCohost'
import { cn } from '@/lib/utils'
import './watch.css'
import './badger.css'

/** His name, everywhere: on his tag and for screen readers. Never "AI" (the founder's rule): he is plainly a character. */
export const BADGER_NAME = 'Badger'

/** Which face size he takes: his own (a co-host's) on the stage, or another seat's for a story. */
export type BadgerSeatSize = 'badger' | 'lead' | 'co-host' | 'guest'

export interface BadgerAvatarProps {
  /** `lk.agent.state`: thinking shows the working frame. */
  state?: CohostState | null
  /** His voice reaches the audience: the green speaking ring, and the mouth frames where motion is welcome. */
  speaking?: boolean
  /** His audio track is muted: the mic glyph, as for a muted host. */
  muted?: boolean
  /** His audio track while this tab receives it: the mouth follows its level (else a gentle cadence). */
  track?: RemoteAudioTrack | null
  /** The size he takes, as the faces' (watch.css): his own by default, a co-host's. */
  seat?: BadgerSeatSize
  /** Held up a moment after he last spoke, as a host's face is (`StageFaces`): the small bob. */
  lifted?: boolean
  /** His tag under the face, "Badger". */
  tag?: boolean
  className?: string
  /** For a fixed size: `--watch-face-size`. */
  style?: CSSProperties
}

/** What LiveKit says of him, in words beside his name: "speaking", "thinking", "muted". */
function stateWords({ speaking, state, muted }: { speaking: boolean; state: CohostState | null; muted: boolean }) {
  return [speaking && 'speaking', state === 'thinking' && 'thinking', muted && 'muted'].filter(Boolean).join(', ')
}

/**
 * Badger's face (docs/features/badger-cohost.md, Identity): a circle seat like the hosts' faces, at their sizes and with
 * their ring, glyph and tag (watch.css), his plush frame on the Play surface colour inside it. Listening at rest; while
 * he speaks the mouth frames follow his voice and the stage's green ring shows; the working frame while he thinks; the
 * mic glyph while he is muted. Reduced motion: the listening frame, and the ring alone says he talks. Named "Badger".
 * The frame comes from his state (`useBadgerFrame`); `BadgerFace` draws it.
 */
export function BadgerAvatar({ state = null, speaking = false, track = null, ...face }: BadgerAvatarProps) {
  const frame = useBadgerFrame({ state, speaking, track })
  return <BadgerFace {...face} frame={frame} state={state} speaking={speaking} />
}

export interface BadgerFaceProps extends Omit<BadgerAvatarProps, 'track'> {
  /** The frame to show. */
  frame: BadgerFrame
}

/**
 * Badger's face with a given frame (BadgerAvatar picks it from his state and voice; a story can name one). Every frame
 * is in the circle from the start, stacked, so each is loaded before it shows and a swap never flashes.
 */
export function BadgerFace({ frame, state = null, speaking = false, muted = false, seat = 'badger', lifted = false, tag = true, className, style }: BadgerFaceProps) {
  const words = stateWords({ speaking, state, muted })
  return (
    <span
      className={cn('watch-face badger-face', className)}
      data-seat={seat}
      data-badger=""
      data-speaking={speaking}
      data-lifted={lifted || undefined}
      data-frame={frame}
      style={style}
    >
      <span className="watch-face__ring" aria-hidden="true" />
      <span className="watch-face__picture badger-face__picture" role="img" aria-label={BADGER_NAME}>
        {BADGER_FRAMES.map((each) => (
          <img
            key={each}
            className="badger-face__frame"
            src={BADGER_FRAME_URLS[each]}
            alt=""
            draggable={false}
            data-frame={each}
            data-shown={each === frame || undefined}
          />
        ))}
      </span>
      {/* Read after his name when browsing the faces, never announced on its own (not a live region). */}
      {words && <span className="sr-only">{words}</span>}
      {muted && (
        <span className="watch-face__glyph watch-face__mic-off" aria-hidden="true">
          <MicOff />
        </span>
      )}
      {tag && (
        <span className="watch-face__tag" aria-hidden="true">
          {BADGER_NAME}
        </span>
      )}
    </span>
  )
}

/** His listening frame as a glyph in a button (the console's Bring in Badger), decorative: the button names the action. */
export function BadgerGlyph({ className }: { className?: string }) {
  return <img className={cn('badger-glyph', className)} src={BADGER_FRAME_URLS.listening} alt="" aria-hidden="true" draggable={false} />
}
